import {
  BigQueryTable,
  GroupBy,
  IComputedAttribute,
  IQueryFactory,
  IRawCte,
  IReportingQuery,
  ITableJoin,
  IUnnestQuery,
  SortOrder,
} from '@principle-theorem/reporting/interfaces';
import { AtLeast } from '@principle-theorem/shared';
import { compact, uniq, uniqWith } from 'lodash';
import { BigQuerySQL } from '../big-query-sql';
import { getRowNumberAttribute } from '../helpers';
import { BaseDimensionMeasures, BaseFactMeasures } from './base-measures';

export class QueryFactory implements IQueryFactory {
  constructor(private _query: IReportingQuery) {}

  static fromQuery(query: AtLeast<IReportingQuery, 'table'>): QueryFactory {
    return new QueryFactory({ attributes: [], ...query });
  }

  static fromTable(table: BigQueryTable): QueryFactory {
    return new QueryFactory({ attributes: [], table });
  }

  static fromMeasures(
    table: BigQueryTable,
    measure: string[] | string,
    unnest?: IUnnestQuery
  ): QueryFactory {
    if (!Array.isArray(measure)) {
      measure = [measure];
    }
    const query = this.fromTable(table).attributes(measure);
    if (unnest) {
      query.unnest(unnest);
    }
    return query;
  }

  get(): IReportingQuery {
    return this._query;
  }

  attributes(measures: string[]): this {
    this._query.attributes = measures;
    return this;
  }

  unnest(unnest: IUnnestQuery): this {
    this._query.unnest = unnest;
    return this;
  }

  mergeJoins(joins?: ITableJoin[]): this {
    this._query.joins = [...(this._query.joins ?? []), ...(joins ?? [])];
    return this;
  }

  override(query: Partial<IReportingQuery>): this {
    this._query = { ...this._query, ...query };
    return this;
  }

  merge(queries: Partial<IReportingQuery>[]): this {
    this._query = queries.reduce((acc: IReportingQuery, query) => {
      const hasJoin = query.joins?.length || acc.joins?.length;
      if (query.table && query.table !== acc.table && !hasJoin) {
        throw new Error(`Cannot merge queries with different tables`);
      }

      if (acc.unnest && query.unnest) {
        if (!isSameUnnest(acc.unnest, query.unnest)) {
          throw new Error(`Cannot merge queries with multiple unnests`);
        }
      }

      const joins = uniqWith(
        [...(acc.joins ?? []), ...(query.joins ?? [])],
        isSameJoin
      );

      const computedAttributes = uniqWith(
        [
          ...(acc.computedAttributes ?? []),
          ...(query.computedAttributes ?? []),
        ],
        isSameComputedAttribute
      );

      const rawCtes = uniqWith(
        [...(acc.rawCtes ?? []), ...(query.rawCtes ?? [])],
        isSameRawCte
      );

      const base: IReportingQuery = {
        ...acc,
        attributes: uniq([...acc.attributes, ...(query.attributes ?? [])]),
        filters: uniq([...(acc.filters ?? []), ...(query.filters ?? [])]),
        joins,
      };
      if (computedAttributes.length) {
        base.computedAttributes = computedAttributes;
      }
      if (rawCtes.length) {
        base.rawCtes = rawCtes;
      }
      if (query.unnest) {
        base.unnest = query.unnest;
      }
      if (query.subQuery) {
        base.subQuery = query.subQuery;
      }
      return base;
    }, this._query);

    return this;
  }

  latestEvent(
    table: BigQueryTable,
    groupBy: GroupBy,
    orderBy?: string,
    sortOrder: SortOrder = SortOrder.Descending
  ): this {
    const rowNumberAlias = 'row_number';
    this._query.subQuery = {
      table,
      attributes: [
        '*',
        getRowNumberAttribute(rowNumberAlias, groupBy, orderBy, sortOrder),
      ],
      filters: [`${rowNumberAlias} = 1`],
    };
    return this;
  }

  filterSoftDeleted(
    dimension: BaseFactMeasures | BaseDimensionMeasures,
    measurePath: string = 'deleted'
  ): this {
    const measure = dimension.measureRef(measurePath);

    this._query.attributes = [measure.attributePath];
    this._query.joins = compact(
      [this._query.joins, dimension.query.joins].flat()
    );

    return this.filter(
      BigQuerySQL.or([
        BigQuerySQL.filter(measure.factPropertyPath, '=', false),
        BigQuerySQL.filter(measure.factPropertyPath, 'IS', undefined),
      ])
    );
  }

  filter(filter: string): this {
    this._query.filters = [...(this._query.filters ?? []), filter];
    return this;
  }
}

function isSameUnnest(aUnnest: IUnnestQuery, bUnnest: IUnnestQuery): boolean {
  return (
    aUnnest.property === bUnnest.property && aUnnest.alias === bUnnest.alias
  );
}

function isSameJoin(aJoin: ITableJoin, bJoin: ITableJoin): boolean {
  return aJoin.alias === bJoin.alias && aJoin.table === bJoin.table;
}

function isSameComputedAttribute(
  aComputed: IComputedAttribute,
  bComputed: IComputedAttribute
): boolean {
  const { alias: aAlias, expression: aExpression } = aComputed;
  const { alias: bAlias, expression: bExpression } = bComputed;

  if (aAlias === bAlias && aExpression !== bExpression) {
    throw new Error(
      'Cannot merge computed attributes with same alias but different expressions'
    );
  }

  return aAlias === bAlias;
}

function isSameRawCte(aCte: IRawCte, bCte: IRawCte): boolean {
  if (aCte.name === bCte.name && aCte.sql !== bCte.sql) {
    throw new Error(
      `Cannot merge raw CTEs with same name but different SQL: "${aCte.name}"`
    );
  }
  return aCte.name === bCte.name;
}
